import React, { useState, useEffect, useRef } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useNavigationWithUserId } from '../utils/navigation'
import NotificationBell from '../components/NotificationBell'
import { 
  Shield, 
  Menu, 
  X, 
  User, 
  Home, 
  FileText, 
  Building2, 
  Settings,
  LogOut,
  Bell,
  CreditCard,
  ChevronDown,
  Activity,
  DollarSign,
  Folder
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { trackNavClick, trackSignOutClick } from '../utils/eventTracking'

const CustomerLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)
  const { user, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { navigateToCustomer, userId } = useNavigationWithUserId()
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Get user_id from current URL if not available in context
  const urlParams = new URLSearchParams(window.location.search)
  const currentUserId = urlParams.get('user_id') || userId

  const navigation = [
    { name: 'Dashboard', href: currentUserId ? `/customer?user_id=${currentUserId}` : '/customer', icon: Home },
    { name: 'My Policies', href: currentUserId ? `/customer/policies?user_id=${currentUserId}` : '/customer/policies', icon: CreditCard },
    { name: 'Claims', href: currentUserId ? `/customer/claims?user_id=${currentUserId}` : '/customer/claims', icon: FileText },
    { name: 'Payments', href: currentUserId ? `/customer/payments?user_id=${currentUserId}` : '/customer/payments', icon: DollarSign },
    { name: 'Documents', href: currentUserId ? `/customer/documents?user_id=${currentUserId}` : '/customer/documents', icon: Folder },
    { name: 'Hospitals', href: currentUserId ? `/customer/hospitals?user_id=${currentUserId}` : '/customer/hospitals', icon: Building2 },
    { name: 'Profile', href: currentUserId ? `/customer/profile?user_id=${currentUserId}` : '/customer/profile', icon: Settings },
    { name: 'API Logs', href: currentUserId ? `/customer/api-logs?user_id=${currentUserId}` : '/customer/api-logs', icon: Activity },
  ]

  const handleSignOut = async () => {
    trackSignOutClick({ area: 'customer' })
    await signOut()
    navigate('/', { replace: true })
  }

  const handleProfileClick = () => {
    navigateToCustomer('profile')
  }

  const isActive = (href) => {
    if (href === '/customer') {
      return location.pathname === '/customer'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 lg:static lg:inset-0`}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <Link to={currentUserId ? `/customer?user_id=${currentUserId}` : '/customer'} className="flex items-center space-x-2">
            <Shield className="h-8 w-8 text-blue-600" />
            <span className="text-xl font-bold text-gray-900">YourInsurance</span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Sidebar Navigation */}
        <nav className="mt-6 px-3">
          <div className="space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  onClick={() => {
                    trackNavClick({ nav_item: item.name, area: 'customer_sidebar' })
                    setSidebarOpen(false)
                  }}
                >
                  <Icon className={`mr-3 h-5 w-5 ${
                    isActive(item.href) ? 'text-blue-700' : 'text-gray-400 group-hover:text-gray-500'
                  }`} />
                  {item.name}
                </Link>
              )
            })}
            
            {/* Sign Out Button */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <button
                onClick={() => {
                  handleSignOut()
                  setSidebarOpen(false)
                }}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-red-600 hover:bg-red-50 transition-colors duration-200"
              >
                <LogOut className="mr-3 h-5 w-5" />
                Sign Out
              </button>
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 lg:border-l lg:border-l-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-500 hover:text-gray-700 p-2"
              >
                <Menu className="h-6 w-6" />
              </button>
              <button
                onClick={() => navigate(currentUserId ? `/customer?user_id=${currentUserId}` : '/customer')}
                className="text-lg font-semibold text-gray-900 ml-2 lg:ml-0 hover:text-blue-600 transition-colors"
              >
                {navigation.find(item => isActive(item.href))?.name || 'Dashboard'}
              </button>
            </div>
            
            <div className="flex items-center space-x-4">
              <NotificationBell userId={currentUserId} />
              
              {/* Profile Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 p-1 hover:bg-gray-100 transition-colors"
                >
                  <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.user_metadata?.first_name || 'Customer'}
                    </p>
                    <p className="text-xs text-gray-500">Customer</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>

                {/* Dropdown Menu */}
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <button
                      onClick={() => {
                        handleProfileClick()
                        setProfileDropdownOpen(false)
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <User className="h-4 w-4 mr-3" />
                      View Profile
                    </button>
                    <button
                      onClick={() => {
                        handleSignOut()
                        setProfileDropdownOpen(false)
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      
      {/* Chat System - Now handled by ChatProvider */}
    </div>
  )
}

export default CustomerLayout